import api from "../../../utils/api";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { User } from "../../../@types/user";
import { RootState } from "../../rootReducer";

function normalizarTexto(texto: string) {
  return texto
    .normalize("NFD")                      // separa letras de sus tildes
    .replace(/[\u0300-\u036f]/g, "")      // elimina tildes
    .replace(/\s+/g, " ")                 // reduce múltiples espacios a uno
    .trim()                               // elimina espacios al inicio y final
    .toLowerCase();                       // convierte todo a minúsculas
}

const findUsers = createAsyncThunk<User[], string | undefined>(
  "users/find",
  async (searchText, { rejectWithValue, getState }) => {
    try {
      // Obtiene el estado actual de la aplicación
      const state = getState() as RootState;
      const cleanSearch = searchText ? normalizarTexto(searchText) : "";

      // Obtiene la lista de usuarios del estado y la ordena por fecha de actualización
      const userList = state.users.users;
      let maxUpdate: number = 0;
      if (userList.length > 0) {
        const orderedList = [...userList].sort((a, b) => {
          if (a.updatedAt && b.updatedAt && a.updatedAt < b.updatedAt) {
            return -1;
          }
          if (a.updatedAt && b.updatedAt && a.updatedAt > b.updatedAt) {
            return 1;
          }
          return 0;
        });
        maxUpdate = orderedList[orderedList.length - 1].updatedAt!;
      }

      // Define un método "findMethod" que realiza una solicitud a la API para buscar usuarios nuevos
      const findMethod = async (model: string, maxUpdate: number): Promise<[User[], number]> => {
        const url = new URL(`${api.baseURL}${model}`);

        // Agrega los parámetros de búsqueda a la URL de la solicitud

        url.searchParams.append("sort", "updatedAt ASC");
        url.searchParams.append("limit", "500");
        url.searchParams.append(
          "sourceOrganization",
          state.userAuthentication.user?.sourceOrganization || "",
        );
        if (cleanSearch) url.searchParams.append("search", cleanSearch);
        url.searchParams.append("from", maxUpdate.toString());
        const token = localStorage.getItem("token");
        const headers = new Headers();
        headers.append("Authorization", `Bearer ${token}`);
        headers.append("Content-Type", "application/json");

        // Realiza la solicitud y devuelve la lista de usuarios nuevos
        let response = await fetch(url.toJSON(), {
          method: "GET",
          headers: headers,
        });
        if (!response.ok) {
          throw response.statusText;
        }
        let data: User[] = await response.json();
        data = data.map(({ id, updatedAt, name, email, dni, isSignatureEnabled, nameNormalized }) => ({
          id,
          updatedAt,
          name,
          email,
          dni,
          isSignatureEnabled,
          nameNormalized,
        })) as User[];

        let newList = [...userList];

        data.forEach((element) => {
          if (newList.find((el) => el.id === element.id)) {
            let user = newList.find((el) => el.id === element.id);
            const indexToUpdate = newList.indexOf(user!);
            newList.splice(indexToUpdate, 1);
            newList.push(element);
          } else {
            newList.push(element);
          }
        });

        let updatedMaxUpdate = maxUpdate;
        if (newList.length > 0) {
          const orderedList = newList.sort((a, b) => {
            if (a.updatedAt && b.updatedAt && a.updatedAt < b.updatedAt) {
              return -1;
            }
            if (a.updatedAt && b.updatedAt && a.updatedAt > b.updatedAt) {
              return 1;
            }
            return 0;
          });
          updatedMaxUpdate = orderedList[orderedList.length - 1].updatedAt!;
        }

        return [newList, updatedMaxUpdate];
      };

      // Busca nuevos usuarios utilizando "findMethod" y actualiza la lista de usuarios existentes
      let users: User[] = [];
      let hasMoreResults = true;

      while (hasMoreResults) {
        const [newUsers, nextMaxUpdate] = await findMethod("user/get-all", maxUpdate);
        users.push(...newUsers);
        if (newUsers.length === 0 || nextMaxUpdate === maxUpdate || nextMaxUpdate < maxUpdate) {
          hasMoreResults = false;
        }
        maxUpdate = nextMaxUpdate;
      }

      if (cleanSearch) {
        users = users.filter((u) => u.nameNormalized?.includes(cleanSearch));
      }

      // Devuelve la lista de usuarios actualizada, en el reducer mismo se realiza la logica para no agregar usuarios repetidos
      return users;
    } catch (error) {
      console.error(error);
      return rejectWithValue(error);
    }
  },
);

export default findUsers;
